import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const REASON_LABELS = {
  unanswered: "Senza risposta",
  not_enough_information: "Non ho elementi per rispondere",
};

function AreaBar({ area }) {
  const pct = area.complete && area.mean != null ? ((area.mean - 1) / 4) * 100 : null;
  return (
    <div className="flex-1 min-w-0" data-testid={`profile-bar-${area.area}`}>
      <div className="relative h-2.5 rounded-full bg-slate-200" role="img"
        aria-label={area.complete ? `${area.label}: ${area.mean_display} su 5` : `${area.label}: compilazione incompleta`}>
        {pct !== null && (
          <>
            <div className="absolute inset-y-0 left-0 rounded-full bg-[#003366] motion-safe:transition-[width] motion-safe:duration-500"
              style={{ width: `${pct}%` }} />
            <div className="absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full border-2 border-[#003366] bg-white"
              style={{ left: `calc(${pct}% - 7px)` }} />
          </>
        )}
      </div>
      <div className="relative mt-1 h-4 text-[10px] text-slate-400 select-none" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((t) => (
          <span key={t} className="absolute -translate-x-1/2" style={{ left: `${((t - 1) / 4) * 100}%` }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

export function MotivationalProfile({ profile, showAnswers = true }) {
  const [openArea, setOpenArea] = useState(null);
  if (!profile?.areas?.length) return null;
  const answers = profile.answers || [];

  return (
    <div data-testid="motivational-profile">
      <p className="text-xs text-slate-500 mb-5" data-testid="motivational-profile-subtitle">
        Sintesi delle risposte dichiarate dal candidato. Supporto al colloquio, senza punteggio automatico in graduatoria.
      </p>
      <div className="space-y-5">
        {profile.areas.map((area) => (
          <div key={area.area} data-testid={`profile-row-${area.area}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">{area.area} · {area.label}</p>
                <p className="text-xs text-slate-500">{area.description}</p>
              </div>
              <div className="text-right shrink-0">
                {area.complete ? (
                  <p className="font-mono2 text-lg font-bold text-[#003366]" data-testid={`profile-value-${area.area}`}>
                    {area.mean_display} <span className="text-xs font-normal text-slate-500">su 5</span>
                  </p>
                ) : (
                  <p className="text-sm font-medium text-slate-500" data-testid={`profile-value-${area.area}`}>
                    Compilazione incompleta
                  </p>
                )}
                <p className="text-[11px] text-slate-400" data-testid={`profile-coverage-${area.area}`}>
                  {area.answered}/6 risposte
                </p>
              </div>
            </div>
            <div className="mt-2">
              <AreaBar area={area} />
            </div>
            {showAnswers && (
              <div className="mt-1">
                <button type="button" data-testid={`profile-answers-toggle-${area.area}`}
                  onClick={() => setOpenArea(openArea === area.area ? null : area.area)}
                  className="text-xs font-medium text-[#003366] hover:underline inline-flex items-center gap-1">
                  {openArea === area.area ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  Vedi le risposte
                </button>
                {openArea === area.area && (
                  <ul className="mt-2 space-y-1.5 border-l-2 border-slate-100 pl-3" data-testid={`profile-answers-${area.area}`}>
                    {answers.filter((ans) => ans.area === area.area).map((ans) => (
                      <li key={ans.code} className="flex items-start justify-between gap-3 text-xs">
                        <span className="text-slate-600">
                          <span className="font-mono2 text-slate-400 mr-1.5">{ans.code}</span>{ans.text}
                        </span>
                        <span className="shrink-0 font-mono2 font-semibold text-slate-700">
                          {ans.value ? `${ans.value} · ${ans.scale_label}` : (REASON_LABELS[ans.reason] || "—")}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="mt-5 text-xs text-slate-500 border-t border-slate-100 pt-3 leading-relaxed" data-testid="motivational-profile-note">
        I valori indicano quanto il candidato si riconosce nelle affermazioni. Un valore più alto non certifica
        maggiori capacità, idoneità o probabilità di completare il percorso. Approfondire le risposte nel colloquio.
      </p>
    </div>
  );
}
